import type { MachineContext } from 'typings/app/machineContext'
import { appViewMessageHandler } from 'lib/window/webViewUtils'
import { getMultiCartItems } from 'api/cart/getMultiCartItems'
import { getMultiCartSummaries } from 'api/cart/getMultiCartSummaries'
import { getMultiCartMembers } from 'api/cart/getMultiCartMembers'
import type { DoneActorEvent, ErrorActorEvent } from 'xstate'
import { assertEvent, assign, fromPromise, log, setup, spawnChild, sendTo, or } from 'xstate'
import type { MultiCartInvitationUsers } from 'api/cart/inviteUsersToMultiCart'
import { inviteUsersToMultiCart } from 'api/cart/inviteUsersToMultiCart'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { createMultiCart } from 'api/cart/createMultiCart'
import { addItemsToMultiCart } from 'api/cart/addItemsToMultiCart'
import { joinUserToMultiCart } from 'api/cart/joinUserToMultiCart'
import { buildUrl } from 'lib/url/searchUrlUtils'
import { leaveMultiCart } from 'api/cart/leaveMultiCart'
import type { RevokeUserAccessParams } from 'api/cart/revokeUserAccessMultiCart'
import { revokeUserAccessMultiCart } from 'api/cart/revokeUserAccessMultiCart'
import { get as getLocalStorage, set as setLocalStorage } from 'lib/storage/isomorphicLocalStorage'
import { moveItemsToTargetCart } from 'api/cart/moveItemsToTargetCart'
import { createAndMoveItemsToTargetCart } from 'api/cart/createAndMoveItemsToTargetCart'
import { softDeleteItemsFromMultiCart } from 'api/cart/softDeleteItemsFromMultiCart'
import { purchaseMultiCartItems } from 'api/cart/purchaseMultiCartItems'
import { multiCartCreateCartEvent, multiCartDeleteItemEvent, multiCartSaveAllForLaterEvent, multiCartSaveMultipleItemsForLaterEvent } from 'analytics/snowplow/events'
import { multiCartDeleteItemMachine } from 'components/Pages/UserCartPage/SharedCarts/multiCartDeleteItemMachine'
import { getCartDisplayName, updateCartItemsInCart, getHasItemsToAdd, getDedupedItemsForCart } from './cartMachineUtils'
import { reportClientError } from 'services/errorReportingService'
import { errorSnackbar, infoSnackbar, successIcon, successSnackbar } from './snackbarUtils'
import { MAX_COMPARISON_SELECTIONS } from 'constants/offer'
import { createCartOfferComparison } from 'api/cartOfferComparison'
import { getMultiCartOfferComparison } from 'api/cart/getMultiCartOfferComparison'

const ACTIVE_CART_ID_STORAGE_KEY = 'sharedCarts.activeCartId'
export const CART_MODAL_EVENT_NAME = 'multiCartMachineModal'

/**
 * State machine docs - https://stately.ai/docs/state-machines-and-statecharts
 */

const multiCartMachine = setup({
  types: {
    context: {} as MachineContext.MultiCartMachineContext,
    events: {} as MachineContext.MultiCartMachineEvents,
    input: {} as MachineContext.MultiCartMachineInput,
  },
  actors: {
    createMultiCart: fromPromise(({ input }: { input: { cartName: string, items: Array<App.Checkout.AnyItem> } }) => createMultiCart(input.cartName, input.items)),
    addItemsToMultiCart: fromPromise(({ input }: { input: { cartId: string, items: Array<App.Checkout.AnyItem>, requestConnectedRooms?: boolean, skipNotification: boolean } }) => addItemsToMultiCart(input.cartId, input.items, input.requestConnectedRooms ?? false, input.skipNotification)),
    leaveMultiCart: fromPromise(({ input }: { input: { cartId: string } }) => leaveMultiCart(input.cartId)),
    getMultiCarts: fromPromise(() => getMultiCartSummaries()),
    getMultiCartItems: fromPromise(({ input }: { input: Array<string> }) => Promise.all(input.map(getMultiCartItems))),
    getMultiCartMembers: fromPromise(({ input }: { input: Array<string> }) => Promise.all(input.map(getMultiCartMembers))),
    inviteUsersToMultiCart: fromPromise(({ input }: { input: { cartId: string, users: MultiCartInvitationUsers } }) => inviteUsersToMultiCart(input.cartId, input.users)),
    joinUserToMultiCart: fromPromise(({ input }: { input: { sharedCartId: string } }) => joinUserToMultiCart(input.sharedCartId)),
    revokeUserAccessMultiCart: fromPromise(({ input }: { input: RevokeUserAccessParams }) => revokeUserAccessMultiCart(input)),
    moveItemsToTargetCart: fromPromise(({ input }: { input: { sourceCartId: string, targetCartId: string, itemIdsToMove: Array<string> } }) => moveItemsToTargetCart(input)),
    createAndMoveItemsToTargetCart: fromPromise(({ input }: { input: { sourceCartId: string, cartName: string, itemIdsToMove: Array<string>, skipNotification: boolean } }) => createAndMoveItemsToTargetCart(input)),
    deleteItemsInMultiCart: fromPromise(({ input }: { input: { cartId: string, itemIdsToDelete: Array<string> } }) => softDeleteItemsFromMultiCart(input.cartId, input.itemIdsToDelete)),
    purchaseMultiCartItems: fromPromise(({ input }: { input: { multiCartId: string, checkoutCartId: string, orderId: string } }) => purchaseMultiCartItems(input.multiCartId, input.checkoutCartId, input.orderId)),
    fetchCartData: fromPromise(async({ input }: { input: { cartIds: Array<string>, shouldFetchItems: boolean, shouldFetchMembers: boolean } }) => {
      const [cartItemsResult, cartMembersResult] = await Promise.allSettled([
        input.shouldFetchItems ? Promise.all(input.cartIds.map(getMultiCartItems)) : Promise.resolve([]),
        input.shouldFetchMembers ? Promise.all(input.cartIds.map(getMultiCartMembers)) : Promise.resolve([]),
      ])

      return {
        cartItems: cartItemsResult.status === 'fulfilled' ? cartItemsResult.value : [],
        cartMembers: cartMembersResult.status === 'fulfilled' ? cartMembersResult.value : [],
        cartItemsError: cartItemsResult.status === 'rejected' ? cartItemsResult.reason : undefined,
        cartMembersError: cartMembersResult.status === 'rejected' ? cartMembersResult.reason : undefined,
      }
    }),
    fetchMultiCartActiveComparisons: fromPromise(() => getMultiCartOfferComparison()),
    createCartOfferComparison: fromPromise(async({ input }: { input: { cartId: string, selections: Array<App.OfferComparisonSelection> } }) => {
      const result = await createCartOfferComparison(input.selections, input.cartId)
      return { ...result, cartId: input.cartId }
    }),
  },
  actions: {
    assignLoggedInMemberId: assign({
      memberId: ({ event }: { event: { type: 'successful login', memberId: string } }) => {
        return event.memberId
      },
    }),
    assignLoggedOutMemberId: assign({
      memberId: () => undefined,
    }),
    assignCartSummary: assign({
      cartSummary: ({ event }: { event: DoneActorEvent<MachineContext.CartSummary, string> }) => ({
        status: 'loaded' as const,
        data: event.output,
      }),
    }),
    assignCartSummaryFetchError: assign({
      cartSummary: () => ({
        status: 'error' as const,
        error: 'Failed to load cart summary',
      }),
    }),
    assignCartItems: assign({
      cartItems: ({ event }: { event: DoneActorEvent<Array<MachineContext.CartItems>, string> }) => {
        // Merge all cart items from Promise.all results into a single object
        return {
          status: 'loaded' as const,
          data: event.output.reduce((acc, cartItems) => ({ ...acc, ...cartItems }), {} as MachineContext.CartItems),
        }
      },
    }),
    emptyCartItemsOfActiveCart: assign({
      cartItems: ({ context }: { context: MachineContext.MultiCartMachineContext }) => {
        const { activeCartId } = context
        if (!activeCartId) {
          return context.cartItems
        }

        return {
          ...context.cartItems,
          data: {
            ...(context.cartItems.status === 'loaded' ? context.cartItems.data : {}),
            [activeCartId]: {},
          },
        }
      },
    }),
    appendCartItems: assign({
      cartItems: ({ event, context }: { event: DoneActorEvent<Array<MachineContext.CartItems>, string>, context: MachineContext.MultiCartMachineContext }) => {
        const newCartItems = event.output.reduce((acc, cartItems) => ({ ...acc, ...cartItems }), {} as MachineContext.CartItems)

        return {
          status: 'loaded' as const,
          data: {
            ...context.cartItems.status === 'loaded' ? context.cartItems.data : {},
            ...newCartItems,
          },
        }
      },
    }),
    processFetchedCartData: assign(({ event, context }, params?: { merge?: boolean }) => {
      const shouldMerge = params?.merge ?? false

      if (!event.type.startsWith('xstate.done.actor') || !('output' in event)) {
        return {}
      }

      const doneEvent = event as unknown as DoneActorEvent<{
        cartItems: Array<MachineContext.CartItems>
        cartMembers: Array<MachineContext.CartMembers>
        cartItemsError?: Error
        cartMembersError?: Error
      }, string>

      if (!doneEvent.output) return {}

      const existingItems = shouldMerge && context.cartItems.status === 'loaded' ? context.cartItems.data : {}
      const existingMembers = shouldMerge && context.cartMembers.status === 'loaded' ? context.cartMembers.data : {}

      return {
        cartItems: doneEvent.output.cartItemsError ? (
          shouldMerge ? context.cartItems : { status: 'error' as const, error: 'Failed to load cart items' }
        ) : {
          status: 'loaded' as const,
          data: {
            ...existingItems,
            ...doneEvent.output.cartItems.reduce((acc, items) => ({ ...acc, ...items }), {}),
          },
        },
        cartMembers: doneEvent.output.cartMembersError ? (
          shouldMerge ? context.cartMembers : { status: 'error' as const, error: 'Failed to load cart members' }
        ) : {
          status: 'loaded' as const,
          data: {
            ...existingMembers,
            ...doneEvent.output.cartMembers.reduce((acc, members) => ({ ...acc, ...members }), {}),
          },
        },
      }
    }),
    appendItemsToCart: assign({
      cartItems: ({ event, context }: { event: DoneActorEvent<{ cartItems: MachineContext.CartItems }, string>, context: MachineContext.MultiCartMachineContext }) => {
        const newCartItems = event.output.cartItems
        const existingData = context.cartItems.status === 'loaded' ? context.cartItems.data : {}
        const updatedData = { ...existingData }

        Object.keys(newCartItems).forEach(cartId => {
          const existingItems = existingData[cartId] ?? {}
          const newItems = newCartItems[cartId]

          // For experience items, deduplicate by item.itemId to prevent
          // duplicates when updateCheckoutItem calls addToUserCart.
          // svc-cart always returns a new backend id on POST, so we need
          // to remove old entries that share the same frontend itemId.
          const newExperienceItemIds = new Set(
            Object.values(newItems)
              .filter(item => item.item.itemType === 'experience')
              .map(item => item.item.itemId),
          )

          const cleanedExisting = newExperienceItemIds.size > 0
            ? Object.fromEntries(
                Object.entries(existingItems).filter(([_, existingItem]) => {
                  if (existingItem.item.itemType !== 'experience') return true
                  return !newExperienceItemIds.has(existingItem.item.itemId)
                }),
              )
            : existingItems

          updatedData[cartId] = {
            ...cleanedExisting,
            ...newItems,
          }
        })

        return {
          status: 'loaded' as const,
          data: updatedData,
        }
      },
    }),
    appendCartMembers: assign({
      cartMembers: ({ event, context }: { event: DoneActorEvent<Array<MachineContext.CartMembers>, string>, context: MachineContext.MultiCartMachineContext }) => {
        const newCartMembers = event.output.reduce((acc, cartMembers) => ({ ...acc, ...cartMembers }), {} as MachineContext.CartMembers)
        return {
          status: 'loaded' as const,
          data: {
            ...context.cartMembers.status === 'loaded' ? context.cartMembers.data : {},
            ...newCartMembers,
          },
        }
      },
    }),
    assignCartItemsFetchError: assign({
      cartItems: () => ({
        status: 'error' as const,
        error: 'Failed to load cart items',
      }),
    }),
    assignCartMembers: assign({
      cartMembers: ({ event }: { event: DoneActorEvent<Array<MachineContext.CartMembers>, string> }) => {
        // Merge all cart members from Promise.all results into a single object
        return {
          status: 'loaded' as const,
          data: event.output.reduce((acc, cartMembers) => ({ ...acc, ...cartMembers }), {} as MachineContext.CartMembers),
        }
      },
    }),
    assignCartMembersFetchError: assign({
      cartMembers: () => ({
        status: 'error' as const,
        error: 'Failed to load cart members',
      }),
    }),
    assignNewlyCreatedCartSummary: assign({
      cartSummary: ({ event, context }: { event: DoneActorEvent<{ createdCart: App.MultiCartSummary, targetCartId: string }, string>, context: MachineContext.MultiCartMachineContext }) => {
        return {
          status: 'loaded' as const,
          data: {
            ...context.cartSummary.status === 'loaded' ? context.cartSummary.data : {},
            [event.output.targetCartId]: event.output.createdCart,
          },
        }
      },
    }),
    loadActiveCartId: assign({
      activeCartId: ({ context }: { context: MachineContext.MultiCartMachineContext }) => {
        const activeCartIdFromStorage = getLocalStorage(ACTIVE_CART_ID_STORAGE_KEY)

        if (
          context.cartSummary.status === 'loaded' &&
          context.cartSummary.data &&
          activeCartIdFromStorage &&
          context.cartSummary.data[activeCartIdFromStorage]
        ) {
          return activeCartIdFromStorage
        }

        // active card id in storage is not available in the cart summary, set the default cart as active
        if (context.cartSummary.status === 'loaded' && context.cartSummary.data) {
          const data = context.cartSummary.data
          const defaultCart = Object.values(data).find(cart => cart.type === 'default')

          if (defaultCart) {
            setLocalStorage(ACTIVE_CART_ID_STORAGE_KEY, defaultCart.cartId)
            return defaultCart.cartId
          }
          const cartSummaryValues = Object.values(data)
          if (cartSummaryValues.length > 0 && cartSummaryValues[0]) {
            setLocalStorage(ACTIVE_CART_ID_STORAGE_KEY, cartSummaryValues[0].cartId)
            return cartSummaryValues[0].cartId
          }
        }

        return context.activeCartId
      },
    }),
    assignActiveCartId: assign({
      activeCartId: ({ context }: { context: MachineContext.MultiCartMachineContext }, params: { cartId: string }) => {
        if (context.activeCartId !== params.cartId) {
          setLocalStorage(ACTIVE_CART_ID_STORAGE_KEY, params.cartId)
        }

        return params.cartId
      },
    }),
    removeActiveCart: assign(
      ({ context }: { context: MachineContext.MultiCartMachineContext }) => {
        if (context.cartSummary.status === 'loaded' && context.cartItems.status === 'loaded' && context.cartMembers.status === 'loaded') {
          if (!context.activeCartId) {
            return context
          }

          const defaultCart = Object.values(context.cartSummary.data).find(cart => cart.type === 'default')
          let newActiveCartId = context.activeCartId

          if (defaultCart && defaultCart.cartId !== context.activeCartId) {
            setLocalStorage(ACTIVE_CART_ID_STORAGE_KEY, defaultCart.cartId)

            newActiveCartId = defaultCart.cartId
          }
          // remove object of active cart from cart summary, cart items and cart members
          const { [context.activeCartId]: _removedCartSummary, ...restCartSummary } = context.cartSummary.data
          const { [context.activeCartId]: _removedCartItems, ...restCartItems } = context.cartItems.data
          const { [context.activeCartId]: _removedCartMembers, ...restCartMembers } = context.cartMembers.data

          return {
            activeCartId: newActiveCartId,
            cartSummary: {
              ...context.cartSummary,
              data: restCartSummary,
            },
            cartItems: {
              ...context.cartItems,
              data: restCartItems,
            },
            cartMembers: {
              ...context.cartMembers,
              data: restCartMembers,
            },
          }
        }

        return context
      },
    ),
    assignRevokedUserAccess: assign({
      cartMembers: ({ event, context }: { event: DoneActorEvent<{ cartId: string, user: App.MultiCartMember }, string>, context: MachineContext.MultiCartMachineContext }) => {
        if (context.cartMembers.status !== 'loaded' || (!event.output.user.userId && !event.output.user.email)) {
          return context.cartMembers
        }
        return {
          status: 'loaded' as const,
          data: {
            ...context.cartMembers.data,
            [event.output.cartId]: context.cartMembers.data[event.output.cartId].map(member =>
              member.userId === event.output.user.userId || member.email === event.output.user.email
                ? event.output.user
                : member,
            ),
          },
        }
      },
    }),
    assignUpdatedCartSummary: assign({
      cartSummary: ({ event, context }: { event: MachineContext.UpdateMultiCart, context: MachineContext.MultiCartMachineContext }) => {
        if (context.cartSummary.status !== 'loaded') {
          return context.cartSummary
        }

        return {
          ...context.cartSummary,
          data: {
            ...context.cartSummary.data,
            [event.cartId]: {
              ...context.cartSummary.data[event.cartId],
              name: event.name,
            },
          },
        }
      },
    }),
    updateCartItem: assign({
      cartItems: ({ event, context }: { event: MachineContext.UpdateCartItem, context: MachineContext.MultiCartMachineContext }) => {
        return updateCartItemsInCart(context, event.cartId, items => ({ ...items, [event.multiCartItem.id]: event.multiCartItem }))
      },
    }),
    markItemAsDeleted: assign({
      cartItems: ({ event, context }: { event: MachineContext.MarkItemAsDeleted, context: MachineContext.MultiCartMachineContext }) => {
        assertEvent(event, 'mark item as deleted')
        return updateCartItemsInCart(context, event.cartId, (items) => {
          event.itemIds.forEach(itemId => {
            if (items[itemId]) {
              items[itemId] = { ...items[itemId], state: 'deleted' }
            }
          })
          return items
        })
      },
    }),
    restoreItemStates: assign({
      cartItems: ({ event, context }: { event: MachineContext.RestoreItemStates, context: MachineContext.MultiCartMachineContext }) => {
        assertEvent(event, 'restore item states')
        return updateCartItemsInCart(context, event.cartId, (items) => {
          event.itemIds.forEach(itemId => {
            if (items[itemId]) {
              items[itemId] = { ...items[itemId], state: event.historyStates[itemId] ?? 'active' }
            }
          })
          return items
        })
      },
    }),
    removeItemFromCart: assign({
      cartItems: ({ context }: { context: MachineContext.MultiCartMachineContext }, params: { cartId: string, itemIds: Array<string> }) => {
        return updateCartItemsInCart(context, params.cartId, (items) => {
          params.itemIds.forEach(itemId => {
            delete items[itemId]
          })
          return items
        })
      },
    }),
    updateCartItemsSaveForLater: assign({
      cartItems: ({ event, context }) => {
        assertEvent(event, 'update cart items save for later')

        return updateCartItemsInCart(context, context.activeCartId!, (items) => {
          event.itemIds.forEach(itemId => {
            items[itemId] = { ...items[itemId], state: event.saveForLater ? 'save_for_later' : 'active' }
          })
          return items
        })
      },
    }),
    spawnDeleteItemMachine: spawnChild(multiCartDeleteItemMachine, {
      id: ({ event }: { event: MachineContext.MultiCartMachineEvents }) => {
        assertEvent(event, 'delete items from given cart')
        return `deleteItemMachine-${event.itemIds.join('-')}`
      },
      input: ({ event }: { event: MachineContext.MultiCartMachineEvents }) => {
        assertEvent(event, 'delete items from given cart')

        return {
          cartId: event.cartId,
          itemIds: event.itemIds,
          currentStates: event.currentStates || {},
        }
      },
      // this as any is temporary fix to fix the type error
    } as any),

    sendSoftDeleteEvent: sendTo(({ event }) => {
      assertEvent(event, 'delete items from given cart')
      return `deleteItemMachine-${event.itemIds.join('-')}`
    }, { type: 'soft delete' }),

    sendUndoDeleteItemEvent: sendTo(({ event }) => {
      assertEvent(event, 'undo delete')
      return `deleteItemMachine-${event.itemIds.join('-')}`
    }, { type: 'undo delete' }),
    sendSaveLaterTrackingEvent: ({ event, context }) => {
      assertEvent(event, 'update cart items save for later')

      if (context.cartItems.status !== 'loaded' || !context.activeCartId) {
        return
      }

      const allCartItems = context.cartItems.status === 'loaded' && context.cartItems.data[context.activeCartId] ? context.cartItems.data[context.activeCartId] : {}
      const allItemValues = Object.values(allCartItems).filter(item => item.state === 'active')

      if (allItemValues.length === event.itemIds.length && event.saveForLater) {
        multiCartSaveAllForLaterEvent(event.itemIds.map(itemId => allCartItems[itemId].item))
        return
      }

      multiCartSaveMultipleItemsForLaterEvent(event.itemIds.map(itemId => allCartItems[itemId].item), event.saveForLater)
    },
    trackMultiCartDeleteItemEvent: ({ event, context }) => {
      const removeEvent = event as MachineContext.RemoveItemFromCart
      const { cartId, itemIds } = removeEvent

      // Get items from machine context, not Redux
      if (context.cartItems.status === 'loaded' && context.cartItems.data[cartId]) {
        const cartItems = context.cartItems.data[cartId]

        // Get the actual items being deleted
        const itemsToDelete = itemIds
          .map(itemId => cartItems[itemId])
          .filter(Boolean)

        // Track analytics for each item individually using existing function
        // Note: This is fire-and-forget, but the analytics function is designed to be robust
        itemsToDelete.forEach(item => {
          multiCartDeleteItemEvent(item.item)
        })
      }
    },
    multiCartCreateCartEvent,
    // Notify native app when cart items change (for updating cart badge in WebView navigation)
    cartUpdated: ({ context }: { context: MachineContext.MultiCartMachineContext }) => {
      if (context.headlessMode) {
        appViewMessageHandler('cartUpdated', {})
      }
    },
    // snackbar actions
    showInvitationSuccessSnackbar: () => {
      successSnackbar('Invitations sent!')
    },
    showInvitationErrorSnackbar: () => {
      errorSnackbar('Failed to send invitations')
    },
    showJoinCartSuccessSnackbar: () => {
      successSnackbar('You\'ve successfully joined the cart')
    },
    showJoinCartErrorSnackbar: ({ event }) => {
      assertEvent(event, 'xstate.error.actor.0.multiCartMachine.joiningUserToMultiCart')

      if (event.error?.status === 404) {
        errorSnackbar('Failed to join the cart, cart not found')
      } else {
        errorSnackbar('Failed to join the cart')
      }
    },
    showLeaveCartSuccessSnackbar: () => {
      infoSnackbar('You have left the cart!')
    },
    showLeaveCartErrorSnackbar: () => {
      errorSnackbar('Failed to leave cart')
    },
    showRevokeUserAccessSuccessSnackbar: () => {
      infoSnackbar('User access has been revoked!')
    },
    showRevokeUserAccessErrorSnackbar: () => {
      errorSnackbar('Failed to revoke user access')
    },
    showAddItemsToCartSuccessSnackbar: ({ context, event }) => {
      const newCartItems = (event as DoneActorEvent<{ cartItems: MachineContext.CartItems }, string>).output.cartItems
      const { activeCartId } = context

      if (!activeCartId) {
        return
      }

      const cartName = getCartDisplayName(context, context.activeCartId)

      const items = Object.values(newCartItems[activeCartId])
      const itemIds = items.map(i => i.id)
      const hasMultiCart = context.cartSummary.status === 'loaded' && Object.keys(context.cartSummary.data).length > 1

      const action = hasMultiCart ? {
        label: 'Change cart',
        onAction: () => {
          document.dispatchEvent(new CustomEvent<MachineContext.CartModalConfig>(CART_MODAL_EVENT_NAME, {
            detail: { multiCartItemIdsToMove: itemIds },
          }))
        },
      } : {
        label: 'View cart',
        to: '/cart',
      }

      showSnackbar(`Item added to ${cartName}`, 'success', {
        icon: successIcon,
        action,
      })
    },
    showDeleteAllItemsFromCartSuccessSnackbar: ({ context }) => {
      const cartName = getCartDisplayName(context, context.activeCartId)
      successSnackbar(`All items have been deleted from ${cartName}`)
    },
    showDeleteAllItemsFromCartErrorSnackbar: () => {
      errorSnackbar('Failed to delete all items from cart')
    },
    showMoveItemsToCartSuccessSnackbar: ({ context }) => {
      const cartName = getCartDisplayName(context, context.activeCartId)
      showSnackbar(`Item added to ${cartName}`, 'success', { icon: successIcon, action: { label: 'View cart', to: '/cart' } })
    },
    showAddItemsToCartErrorSnackbar: () => {
      errorSnackbar('Failed to add items to cart')
    },
    showCreateCartSuccessSnackbar: ({ event }) => {
      showSnackbar(`${(event as DoneActorEvent<{ createdCart: App.MultiCartSummary, targetCartId: string }, string>).output.createdCart.name} cart has been created!`, 'success', { icon: successIcon, action: { label: 'View cart', to: '/cart' } })
    },
    showCreateCartErrorSnackbar: () => {
      errorSnackbar('Failed to create cart')
    },
    showCreateCartOfferComparisonSuccessSnackbar: () => {
      successSnackbar('Comparison sent to customer!')
    },
    showCreateCartOfferComparisonErrorSnackbar: () => {
      errorSnackbar('Failed to send comparison')
    },
    pushToCartPage: ({ context }: { context: MachineContext.MultiCartMachineContext }) => {
      context.history.push(buildUrl('/cart', context.currentRegionCode))
    },
    logErrorEvent: ({ event }: { event: MachineContext.MultiCartMachineEvents | ErrorActorEvent<Error, string> }, params?: { operation?: string }) => {
      // Only process ErrorActorEvent types
      if (!('error' in event)) return

      const errorEvent = event as ErrorActorEvent<Error, string>
      const operation = params?.operation || 'multiCart operation'
      reportClientError(errorEvent.error, {
        message: `MultiCart machine error: ${operation}`,
        actorId: errorEvent.actorId,
        stateMachineError: true,
      })
      console.error(`[MultiCart] ${operation} failed:`, errorEvent.error)
    },
    setCartCreationSuccess: assign({
      lastCartCreationSuccess: () => true,
    }),
    clearCartCreationSuccess: assign({
      lastCartCreationSuccess: () => undefined,
    }),
    setCartSharingFlowStatus: assign({
      cartSharingFlowStatus: ({ event: _event }: { event: MachineContext.MultiCartMachineEvents }, params: { status: MachineContext.CartSharingFlowStatus }) => params.status,
    }),
    addCartOfferComparisonSelection: assign({
      cartSpoofedSelections: ({ event, context }: { event: MachineContext.AddCartOfferComparisonSelection, context: MachineContext.MultiCartMachineContext }) => {
        const { cartId, selection } = event
        const existingSelections = context.cartSpoofedSelections.status === 'loaded' ? context.cartSpoofedSelections.data[cartId] || [] : []

        const isSelected = existingSelections.some(s => s.selectionId === selection.selectionId)
        if (isSelected) {
          return context.cartSpoofedSelections
        }

        if (existingSelections.length >= MAX_COMPARISON_SELECTIONS) {
          return context.cartSpoofedSelections
        }

        return {
          status: 'loaded' as const,
          data: {
            ...(context.cartSpoofedSelections.status === 'loaded' ? context.cartSpoofedSelections.data : {}),
            [cartId]: [...existingSelections, selection],
          },
        }
      },
    }),
    removeCartOfferComparisonSelection: assign({
      cartSpoofedSelections: ({ event, context }: { event: MachineContext.RemoveCartOfferComparisonSelection, context: MachineContext.MultiCartMachineContext }) => {
        const { cartId, selectionId } = event
        const existingSelections = context.cartSpoofedSelections.status === 'loaded' ? context.cartSpoofedSelections.data[cartId] || [] : []

        return {
          status: 'loaded' as const,
          data: {
            ...(context.cartSpoofedSelections.status === 'loaded' ? context.cartSpoofedSelections.data : {}),
            [cartId]: existingSelections.filter(s => s.selectionId !== selectionId),
          },
        }
      },
    }),
    assignActiveComparisonFromCreation: assign({
      cartActiveComparisons: ({ event, context }: { event: DoneActorEvent<{ result: { items: Array<App.OfferComparisonSelection> }, cartId: string }, string>, context: MachineContext.MultiCartMachineContext }) => {
        return {
          status: 'loaded' as const,
          data: {
            ...(context.cartActiveComparisons.status === 'loaded' ? context.cartActiveComparisons.data : {}),
            [event.output.cartId]: event.output.result.items,
          },
        }
      },
    }),
    assignActiveComparison: assign({
      cartActiveComparisons: ({ event, context }: { event: DoneActorEvent<MachineContext.CartActiveComparisons, string>, context: MachineContext.MultiCartMachineContext }) => {
        return {
          status: 'loaded' as const,
          data: {
            ...(context.cartActiveComparisons.status === 'loaded' ? context.cartActiveComparisons.data : {}),
            ...event.output,
          },
        }
      },
    }),
    assignActiveComparisonError: assign({
      cartActiveComparisons: () => ({
        status: 'error' as const,
        error: 'Failed to load active comparison',
      }),
    }),
    replaceCartOfferComparisonSelection: assign({
      cartSpoofedSelections: ({ event, context }: { event: MachineContext.ReplaceCartOfferComparisonSelection, context: MachineContext.MultiCartMachineContext }) => {
        const { cartId, selection } = event
        const existingSelections = context.cartSpoofedSelections.status === 'loaded' ? context.cartSpoofedSelections.data[cartId] || [] : []

        return {
          status: 'loaded' as const,
          data: {
            ...(context.cartSpoofedSelections.status === 'loaded' ? context.cartSpoofedSelections.data : {}),
            [cartId]: existingSelections.map(s => s.selectionId === selection.selectionId ? selection : s),
          },
        }
      },
    }),
    // Charter flight pricing actions for Maldives bundle
    setCharterFlightPricingFetching: assign({
      charterFlightPricing: ({ event, context }: { event: MachineContext.SetCharterFlightPricingFetching, context: MachineContext.MultiCartMachineContext }) => ({
        ...context.charterFlightPricing,
        [event.itemId]: {
          ...context.charterFlightPricing[event.itemId],
          fetching: event.fetching,
        },
      }),
    }),
    setCharterFlightDualPricing: assign({
      charterFlightPricing: ({ event, context }: { event: MachineContext.SetCharterFlightDualPricing, context: MachineContext.MultiCartMachineContext }) => ({
        ...context.charterFlightPricing,
        [event.itemId]: {
          bundle: event.bundle,
          standalone: event.standalone,
          fetching: false,
        },
      }),
    }),
    clearCharterFlightPricing: assign({
      charterFlightPricing: ({ event, context }: { event: MachineContext.ClearCharterFlightPricing, context: MachineContext.MultiCartMachineContext }) => {
        const { [event.itemId]: _, ...rest } = context.charterFlightPricing
        return rest
      },
    }),
    clearAllCharterFlightPricing: assign({
      charterFlightPricing: () => ({}),
    }),
  },
  guards: {
    isUserLoggedIn: ({ context }: { context: MachineContext.MultiCartMachineContext }) => !!context.memberId,
    hasSharedCart: ({ event }) => {
      return event.type.startsWith('xstate.done.actor') &&
        'output' in event &&
        event.output &&
        Object.keys(event.output).length > 0
    },
    isSharedCartInvitePage: ({ context }: { context: MachineContext.MultiCartMachineContext }) => context.history.location.pathname.includes('/cart/s/'),
    canAddItemsToActiveCart: ({ context, event }) => {
      if (event.type !== 'add items to active cart' || !('items' in event)) {
        return false
      }
      return getHasItemsToAdd(context, event.items)
    },
  },
},
).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QFsCuAbALgSwMIEMAnTAWXwGMALbAOzADoqxyBrWqAZU30zAGIA2gAYAuolAAHAPaxsOKTXEgAHogCsANgDM9LQE4h27QBYhQ-QBoQAT0QAmO3ru6TARi0bjegBx7XAX38rNCw8IlIKajpGSmY2Gk5uXkFXMSQQaVl5RXTVBDU1HX1DDy1Tcz0rWwR3LSF6DVc9LQB2b3dCu21A4IwcAmIyKloGJlZ2Lh5+ATs0yRk5bAUlPM0igyMys0sbRFdvDXqtOxa1b2NXNRPzrR6QEP7woaiGWkX8dGwAL3Y+CAUGLAkgwHmFBpERvQ3jgPt92MI5hkFtkVvYhK56nZ2lomjizEInFU9q53PQWhorq0hJo9Go9MY7qCBhFhtFodhYT8EvR0FJ8BB2MyOKhkMgiNY-gCoTQAG5SFggvpglkvaXvT5cqA8vkChJCkViwjWBC0OXkHhLGgIhFKTKLZa5YkFMnmY7eOneITeb1EhAaU70DF+b1tYx2dF6RlK5nPSHsznsbX8wXhYWi8V8MCEQhSQj0CToHgAM1zyHoTKeELZNHVcO5vOTetTBvFJtlUnN2WtoltyMtqJqlzULq0bo9Xp9u3yzpaQkcehark8njKUdCMarrxrMI1iYbuqgzIAImBuNh0LBJdEgVNy9HK6yt7XNUmD8fT-hz7Abek7SjHTULRdIGmjhtSFJaN4dhqL67j0vQYauF4UHeEB-p2Gujzgo+ao7nWWr7imxAnmeF6vkRmAAJK8MgsDkQkV5bnKCp3uuD6qvGu71jqFEkZ+ZGEU2xDUWAtH0VAbZmhaCjdoif79gBrjQS09DNKc+xztoTQaLBdhLvQaiXMYEH0mUniYcqsbVs+e48UJmB8V+4nMiJYmCVAmbZrm+aFpgJaEGWFbYRx24clxBF2Ye4SOQJkUuTRdHuZJHbSVaog-vMWQKaAeRKWoKlqYZnpdDieg6VO7gEghGgHI0FLhqhtxBPc97BXGoUJtxjZRcRH5Oe5zIAKqwFmiWRYx0rMYqbFtdZeEvgN0V9bF3VDSNhBjd1yWdpasm9llDo5XsWKuPQvglDii5Qe4sGGadThIahrgtM0ZUWRuOGcfhzlLaRm1vuEw2jeJnk5nmBbFqWrFYSq7U2V1AO9X9P3EEDG3idtqV7b+faHSox3tGdGyjs9+x6VosGHMYZ0tMZ3hlK07g1e97GQoQYD8hKsCoOQ5BwLARYYAABLyUC0BlSIHTkR3TusJQmNslRTnYFz1I0o4GFiWhqGGGHNUFsPROznN8EW-GQCLUhi2lcm49L+MIHULRFP6SEaP6o7hhTU76GVujazr7RmEukb661hsMMbEASqachgELoJC+axBC6g63fj2ONSwOhynedK41XStPe9UkHUvQ5i004hy0gcLOzZHHPR3w5DG7wCdKkn4QS-JeO5YZzh6ahfgLu6ektL6jhqPUzv5Y4xgHIUoe9DNEf0FHEqtxz7f4DQEAJ1IMrx3HtFC5gUhn0QMCYF3xA93bA6QXoCGtIZbQbMZ2iT7TGiqWV4YUhDNseua8N58H5PvE+sAz4XwoDgI+t9MD32zopJCzgrqnA0HpUw+wlbVDsDiam7tNKtEaMYF6ICrKNxNiNdAzAb6733sgQ+x8EowMvoQa+iDkH2ntnkY4w5Zw4kKBpb0phjC+nEb-QotMVbUjpmoShm515NwlOzZhCC4HYAQcnG+2AIA8P-DLOoHhVI3DDH4UwUFfTFCOFBRC+Ug4aCUThMBqAJAQCmB3UI3DM6ZV4Y-Oc9R6Z0kuGVF6QgJE+09NTCxHgNhCDaJoFxqowF0PwAgxOujDHZQdiSemugsR6QIeBb03gbHkwMvSAwiS9D0iuCktmqi+DsymqndaQsKB81gBnW2KCZYaD9qcMwi59jnFpjBZWgzf5eCLn4FWWCFyNKNs0kaDDyDwPjtkvxksAkATqF4Qpnoy5LiuM0Gxs5vAIXJDiCkxgzLkOWdQ5uAArKQtBvE4F8X0vZAzyENAJB4FobRqSL0ngQ65dzR5hJCU8lRJt3GePbrooWJ8cl90QHUQogYF6mFHFg8MBQbGNGcJSJoZhsHohaHCsB6iWGopokLIsOZkDfP2r8h2dRLiBnRFcXwhQyg+BsV4eoJJBnhiUscxRYdV5UPhc3Q0LAGWiU6dAiAYA6G8AMTs3ufDMWV3oCrOpnh7nkjOJM0u+hqZLlaM0Q4Rc9YrxhnK2lcBz7s2Vaym8vBensqMZyyJ1Mq4LjuuYbBFSfCGoxO7S5xRl4tVlcosBokJCYGsIgz10CpBFk6Rs7RWzu46ofgBRwNURwYigguaCeDMXPWHDE-Qi4DA60dQm51SbmmIq8SiqBQtYAZPjv5EWUxCDor1Qgecz8biLgarnLoNjqTrHaCcRwi5TjSqdZZZRW8LQJBIOHCapp5TTXbThHdOA93h0xl2dKRb+kO1pCpRxelzC4IcJPA4Kkf7hmaEzPScLz3sH3avUG3kIZ+ShgbOVgHL2r2vbtW9Pz-X9yUlG4eTQknjw-flMkUEBUOGHu6ADbd2AAEE94kEPuwAAKlIYDMND3thYlB7dJGEjkYgJRmUNG6NXqPTtGSiG-W5P4b4F+6l36GE-iXWt2Kn6k3MAULoG621brPWxqAHGuM8fo8qUD4NfL+UCuHaDGmtNUYSLR3TzJ4OCZEGOx+YnTVv3ztJ30hMvB1O9BSaedTDJwogewVysBaOkbzUfZkjGprQzU6qQLCRguhfC2AGz-GsZCazhyvIKFAyoWnmTYwBQhUVVHmdE4ZwgV0kMK2ljOF4tQES1IMLmzIv-GrNF2rcWIAHka81-NqX2wCZtgIVIwmMUIHplc-YwyCtFfKcrRJp0QWzjpFiAwxgGQytPV1nrCUkstfCPpnykMAoxY+jtoLe2mvJYG1JG99m71ZeJGGMk03QJIXRL4SeTRTr7EGYuAoxxqTUq27FyE6TuOwYY21pix6zus2iBDoDfHBvpYe0hkTewB5oZehhsez0F3okNdrYhv7tDeDhUjqHemsxg2OxB07nXwcc0h1Aaz4RbPDce8h4kBS3TFKxTVScpclJqwuGPAoJJwk0rAMxdgaMwvdMvDDyacOmcrLlwkBXvN+ac+xv4nnfohnT0W2M8hhXJ7GV+7UR9o5IKbc3edppmuoDa6V0d8DRn4cNxUS7t3uu0v3YcwBaZZITejLERMyeZx6iGQXNrDwKssQqfV0xRYDEVdHuYyZ5RscL0SUDwh9HY3x25yJlJjw7oFz3OJRcM6ZQujhmLvSGrOfPqynTx5WnYHDOQbbyFbj+e9cZYN5jhAj6DJtBfTiXw76famF-otoppRmjJNB076IRZTysk0zd8IwWotq-75CLfmAd99Yi-vhKw-i+ZcNxP59GIZ9ODsDYwOZ1sSNBCVgwocLT-n732EgSg9170Z2P0323yiF3wOyANElgBv2DxlgfynyfzfVfx9hxB0HyUgmKUMndj-0gPYAADkwAAB3GMUSAAI1GkP2z0TRwn-ygJIPIKeCoNGgQO5zHyQg21Uhqhb3-jaHKlLnf0iTaBiScE9GcXXwRwYEYOILIIoOQGoI2hAJO2M3oNVDkISGYMUOUPgMLzs0QLyQ22pjKl8AsSwUENr1OmDkSX9Gnh1mdjhTeTeC13WiswPUzyYxPTB2iBcJrDcKzA8LgwMK5wx3G08BUhriBRBTOApHc0aBdE9E+y6DpiakdxkPoH8Pl3cN4xA27wMzUO9zXmyMCMIGCJhg4PCNL3+WiP9FiLBXn2pgcApCHEiSQmBSkIyJ9w0UuzgNo2oyvlPFaylCzx8I3xBAswayu0GM4WGI51CP112UNwEXLREU9DEUiQjWfhVndEMBIV8DhV6ISxmKGMwEiwKPpy91T3LCmMa1mOvluxSiD04PG1WKEUuBnwnHERsXWwMkOAOEcBOjrjuBoCkHVXgHSFTxLwHAAFohDEBYThw6kUTUTUSgIANYhxgEhJheAYT9knNX5npXN3YZMJ01syQTAdYsVaRtY4UvpNR8SZYnpDUw0SlhlFwESEA6ZWTS1QIE8HAHdVMJjcIwpvpFpiA0xDRqhR9xt6RMQ2SnZp5OTfQzhToF9ElLkfNDB6SOpwoUYHJlpITZTx0R5WTjh2TlSlxYJ2iDIjJvQvQLhzddT4YIpVpfp+J-oKJgsmSH1-RzSHAlTFsuSSRHAK4hwcFQVDAujhTMiGTbJ3SkZPSDTgtxJfTcpeUAzLTgzdIsQ-5wxyFtBChCsXT5oEzEZDTkYJSqIEp6AYd0y9gvAdBlMKQ2ggJHTXBdJTE6k9J8pxVfMhSbj4yEZeIjSUzayCiGyagmzDUKRWzUIGoLhYJjhn56lgdf1rgYyhy9TxS4oPT+o9zUZ04pzwksygyVSKowxYleyv8DB3QKRSyxSFpDzKzkzqy0YvSEgpyMRuUvZAzEkrSQyrhqZG0FxElzcKhHzOo3SKyYpPyepMAPy6yARvyl4DIcQvZ0RAyJ4KpOhdAfBnp6RgUChDjpCfdhyYLRyqyXykLJy78uC0KRFMKlICEcLqglxoJdBDJalOS3QoL9T9xIBvzzhgkTo-BDhOT6RlynBAxeKzAmymhW8NC4YyzuQ6KTSBwyphw-zsyLz2L0RpEhwsQLhjhjJ0jYyfcN4pzsErlTgWjTBp9PBJFqQ85tZyFaRCsugKcyK14YM2dw5vyCFnBSY-AZ1Zx7lOzlZh4GhgKlJPZHFiNt4yMKMpiKjlQpywxv4IVjU7cvAlIfAtzwCGB6tetADMApy1h8L5YtgKhvsCkDhK8Q5HB9hKcWdkdV4TycNH9X1Z90CRc9IXAQljLtYbUZc-d1pFd+Ypyn5xMXMP5STo9CYFklxTl8QU8irJpO9prOKPj1jvitifZPBSUATShFxCsQdui14tDoD+sr84CpzZwp1TUPY1ThdMU6grlzgF4OyTgwxCrlKICz8mCFDWClDRpvziTDVsRzh7yChDBiUVZVJgVZkCRxdaZnD3kAjXdcj2diBprCSJMSSv4MDVyzkLhccgIryji7jTi5jzjwgMq+rMVm0obTgF4grE91qAbARKAiBIBmRYAAAxbeVAdmI8bAftSguhCAKcr63QYZMNBW04cFMTUoe5O3TBLoQIQIIAA */
  id: 'multiCartMachine',
  context: ({ input }: { input: MachineContext.MultiCartMachineInput }) => ({
    cartSummary: { status: 'loading' as const },
    cartItems: { status: 'loading' as const },
    cartMembers: { status: 'loading' as const },
    cartSpoofedSelections: { status: 'loading' as const },
    cartActiveComparisons: { status: 'loading' as const },
    lastCartCreationSuccess: undefined,
    cartSharingFlowStatus: 'idle' as const,
    charterFlightPricing: {},
    ...input,
  }),
  // Determine initial state - no always transitions at the top level to avoid recursion
  initial: 'checkingState',
  states: {
    checkingState: {
      always: [
        {
          target: 'initializing',
          guard: 'isUserLoggedIn',
        },
        {
          target: 'userIsLoggedOut',
        },
      ],
    },
    initializing: {
      initial: 'loadingCartSummary',
      states: {
        loadingCartSummary: {
          invoke: {
            src: 'getMultiCarts',
            onDone: [{
              guard: or(['hasSharedCart', 'isSharedCartInvitePage']),
              target: 'loadingCartDetails',
              actions: ['assignCartSummary', 'loadActiveCartId'],
            }],
            onError: {
              target: 'error',
              actions: [
                { type: 'logErrorEvent', params: { operation: 'getMultiCartSummary' } },
                'assignCartSummaryFetchError',
              ],
            },
          },
        },
        loadingCartDetails: {
          invoke: {
            src: 'fetchCartData',
            input: ({ context }) => ({
              cartIds: context.cartSummary.status === 'loaded' && context.cartSummary.data ? Object.keys(context.cartSummary.data) : [],
              shouldFetchItems: true,
              shouldFetchMembers: true,
            }),
            onDone: {
              target: 'fetchingActiveComparison',
              actions: 'processFetchedCartData', // merge: false (default) - replaces data during initialization
            },
            onError: {
              target: 'error',
              actions: [{ type: 'logErrorEvent', params: { operation: 'fetchCartData (initialization)' } }],
            },
          },
        },
        fetchingActiveComparison: {
          invoke: {
            src: 'fetchMultiCartActiveComparisons',
            onDone: {
              target: 'loaded',
              actions: 'assignActiveComparison',
            },
            onError: {
              target: 'loaded',
              actions: [
                { type: 'logErrorEvent', params: { operation: 'fetchMultiCartActiveComparisons (initialization)' } },
                'assignActiveComparisonError',
              ],
            },
          },
        },
        loaded: {
          type: 'final',
        },
        error: {
          type: 'final',
        },
      },
      onDone: {
        target: 'ready',
      },
    },
    userIsLoggedOut: {
      on: {
        'successful login': {
          target: 'checkingState',
          actions: 'assignLoggedInMemberId',
        },
        'failed login': {
          target: 'userIsLoggedOut',
        },
      },
    },
    ready: {
      on: {
        'successful login': {
          target: 'checkingState',
          actions: 'assignLoggedInMemberId',
        },
        'failed login': {
          target: 'ready',
        },
        'logout user': {
          target: 'userIsLoggedOut',
          actions: 'assignLoggedOutMemberId',
        },
        'invite multi cart users': {
          target: 'inviting',
        },
        'create and move items to target cart': {
          target: 'creatingAndMovingToMultiCart',
        },
        'add items to active cart': [
          {
            target: 'addingItemsToActiveCart',
            guard: 'canAddItemsToActiveCart',
          },
          {
            target: 'ready',
          },
        ],
        'select and move items to target cart': {
          target: 'movingItemsToTargetCart',
          actions: [log(({ event }) => `** movingItemsToTargetCart ${JSON.stringify(event)}`)],
        },
        'remove active cart id': {
          target: 'ready',
          actions: ['removeActiveCart'],
        },
        'update multi cart': {
          target: 'ready',
          actions: 'assignUpdatedCartSummary',
        },
        'leave multi cart': {
          target: 'leavingMultiCart',
        },
        'revoke user access': {
          target: 'revokingUserAccess',
        },
        'set active cart': {
          target: 'ready',
          actions: {
            type: 'assignActiveCartId',
            params: ({ event }: { event: MachineContext.SetActiveCart }) => ({ cartId: event.cartId }),
          },
        },
        'join multi cart': {
          target: 'joiningUserToMultiCart',
        },
        'update cart item': {
          target: 'ready',
          actions: 'updateCartItem',
        },
        'remove item from cart': {
          target: 'ready',
          actions: [
            'trackMultiCartDeleteItemEvent',
            { type: 'removeItemFromCart', params: ({ event }: { event: MachineContext.RemoveItemFromCart }) => ({ cartId: event.cartId, itemIds: event.itemIds }) },
            'cartUpdated',
          ],
        },
        'mark item as deleted': {
          target: 'ready',
          actions: 'markItemAsDeleted',
        },
        'restore item states': {
          target: 'ready',
          actions: 'restoreItemStates',
        },
        'empty cart items of active cart': {
          target: 'ready',
          actions: ['emptyCartItemsOfActiveCart', 'showDeleteAllItemsFromCartSuccessSnackbar', 'cartUpdated'],
        },
        'update cart items save for later': {
          target: 'ready',
          actions: ['sendSaveLaterTrackingEvent', 'updateCartItemsSaveForLater', 'cartUpdated'],
        },
        'purchase active cart items': {
          target: 'purchasingActiveCartItems',
        },
        'delete items from given cart': {
          target: 'ready',
          actions: ['spawnDeleteItemMachine', 'sendSoftDeleteEvent'],
        },
        'undo delete': {
          target: 'ready',
          actions: ['sendUndoDeleteItemEvent'],
        },
        'clear cart creation success': {
          target: 'ready',
          actions: 'clearCartCreationSuccess',
        },
        'sharing flow begin': {
          target: 'ready',
          actions: { type: 'setCartSharingFlowStatus', params: { status: 'creating' } },
        },
        'sharing flow ends': {
          target: 'ready',
          actions: { type: 'setCartSharingFlowStatus', params: { status: 'idle' } },
        },
        'add cart offer comparison selection': {
          target: 'ready',
          actions: 'addCartOfferComparisonSelection',
        },
        'remove cart offer comparison selection': {
          target: 'ready',
          actions: 'removeCartOfferComparisonSelection',
        },
        'fetch cart active comparison': {
          target: 'fetchingActiveComparison',
        },
        'create cart offer comparison': {
          target: 'creatingCartOfferComparison',
        },
        'replace cart offer comparison selection': {
          target: 'ready',
          actions: 'replaceCartOfferComparisonSelection',
        },
        // Charter flight pricing events for Maldives bundle
        'set charter flight pricing fetching': {
          target: 'ready',
          actions: 'setCharterFlightPricingFetching',
        },
        'set charter flight dual pricing': {
          target: 'ready',
          actions: 'setCharterFlightDualPricing',
        },
        'clear charter flight pricing': {
          target: 'ready',
          actions: 'clearCharterFlightPricing',
        },
        'clear all charter flight pricing': {
          target: 'ready',
          actions: 'clearAllCharterFlightPricing',
        },
      },
    },
    creatingAndMovingToMultiCart: {
      invoke: {
        src: 'createAndMoveItemsToTargetCart',
        input: ({ event, context }) => {
          assertEvent(event, 'create and move items to target cart')

          return {
            sourceCartId: context.activeCartId!,
            cartName: event.cartName,
            itemIdsToMove: event.multiCartItemIdsToMove,
            skipNotification: event.skipNotification,
          }
        },
        onDone: [{
          target: 'fetchingCartData',
          actions: [
            { type: 'removeItemFromCart', params: ({ event }: { event: DoneActorEvent<{ cartId: string, itemIds: Array<string> }, string> }) => ({ cartId: event.output.cartId, itemIds: event.output.itemIds }) },
            { type: 'assignActiveCartId', params: ({ event }: { event: DoneActorEvent<{ createdCartId: App.MultiCartSummary, targetCartId: string }, string> }) => ({ cartId: event.output.targetCartId }) },
            'assignNewlyCreatedCartSummary',
            'multiCartCreateCartEvent',
            'setCartCreationSuccess',
            { type: 'setCartSharingFlowStatus', params: { status: 'inviting' } },
            'cartUpdated',
          ],
          guard: ({ event }) => {
            assertEvent(event, ['xstate.done.actor.0.multiCartMachine.creatingAndMovingToMultiCart'])
            return event.output.skipNotification
          },
        },
        {
          target: 'fetchingCartData',
          actions: [
            'showCreateCartSuccessSnackbar',
            { type: 'removeItemFromCart', params: ({ event }: { event: DoneActorEvent<{ cartId: string, itemIds: Array<string> }, string> }) => ({ cartId: event.output.cartId, itemIds: event.output.itemIds }) },
            { type: 'assignActiveCartId', params: ({ event }: { event: DoneActorEvent<{ createdCartId: App.MultiCartSummary, targetCartId: string }, string> }) => ({ cartId: event.output.targetCartId }) },
            'assignNewlyCreatedCartSummary',
            'setCartCreationSuccess',
            { type: 'setCartSharingFlowStatus', params: { status: 'inviting' } },
            'cartUpdated',
          ],
        }],
        onError: {
          target: 'ready',
          actions: [
            { type: 'logErrorEvent', params: { operation: 'createAndMoveItemsToTargetCart' } },
            'showCreateCartErrorSnackbar',
            { type: 'setCartSharingFlowStatus', params: { status: 'failed to create' } },
          ],
        },
      },
    },
    addingItemsToActiveCart: {
      invoke: {
        src: 'addItemsToMultiCart',
        input: ({ context, event }) => {
          assertEvent(event, ['add items to active cart'])
          return {
            cartId: context.activeCartId!,
            items: getDedupedItemsForCart(context, event.items),
            requestConnectedRooms: event.requestConnectedRooms,
            skipNotification: event.skipNotification,
          }
        },
        onDone: [{
          target: 'ready',
          actions: ['appendItemsToCart', 'cartUpdated'],
          guard: ({ event }) => {
            assertEvent(event, ['xstate.done.actor.0.multiCartMachine.addingItemsToActiveCart'])
            return event.output.skipNotification
          },
        },
        {
          target: 'ready',
          actions: ['showAddItemsToCartSuccessSnackbar', 'appendItemsToCart', 'cartUpdated'],
        }],
        onError: {
          target: 'ready',
          actions: [
            { type: 'logErrorEvent', params: { operation: 'addItemsToMultiCart' } },
            'showAddItemsToCartErrorSnackbar',
          ],
        },
      },
    },
    leavingMultiCart: {
      invoke: {
        src: 'leaveMultiCart',
        input: ({ event }) => {
          assertEvent(event, 'leave multi cart')
          return {
            cartId: event.cartId,
          }
        },
        onDone: {
          target: 'ready',
          actions: ['removeActiveCart', 'showLeaveCartSuccessSnackbar'],
        },
        onError: {
          target: 'ready',
          actions: [
            { type: 'logErrorEvent', params: { operation: 'leaveMultiCart' } },
            'showLeaveCartErrorSnackbar',
          ],
        },
      },
    },
    revokingUserAccess: {
      invoke: {
        src: 'revokeUserAccessMultiCart',
        input: ({ event }) => {
          assertEvent(event, 'revoke user access')
          if (event.userId) {
            return {
              cartId: event.cartId,
              userId: event.userId,
            }
          } else if (event.email) {
            return {
              cartId: event.cartId,
              email: event.email,
            }
          } else {
            throw new Error('Either userId or email must be provided for revoke user access')
          }
        },
        onDone: {
          target: 'ready',
          actions: ['showRevokeUserAccessSuccessSnackbar', 'assignRevokedUserAccess'],
        },
        onError: {
          target: 'ready',
          actions: [
            { type: 'logErrorEvent', params: { operation: 'revokeUserAccessMultiCart' } },
            'showRevokeUserAccessErrorSnackbar',
          ],
        },
      },
    },
    inviting: {
      invoke: {
        src: 'inviteUsersToMultiCart',
        input: ({ event }) => {
          assertEvent(event, 'invite multi cart users')
          return {
            cartId: event.cartId,
            users: event.users,
          }
        },
        onDone: {
          target: 'fetchingCartData',
          actions: [
            'showInvitationSuccessSnackbar',
            { type: 'setCartSharingFlowStatus', params: { status: 'idle' } },
          ],
        },
        onError: {
          target: 'ready',
          actions: [
            { type: 'logErrorEvent', params: { operation: 'inviteUsersToMultiCart' } },
            'showInvitationErrorSnackbar',
            { type: 'setCartSharingFlowStatus', params: { status: 'failed to invite' } },
          ],
        },
      },
    },
    fetchingCartData: {
      invoke: {
        src: 'fetchCartData',
        input: ({ context }) => ({
          cartIds: [context.activeCartId!],
          shouldFetchItems: true,
          shouldFetchMembers: true,
        }),
        onDone: {
          target: 'ready',
          actions: { type: 'processFetchedCartData', params: { merge: true } }, // Merges with existing data
        },
        onError: {
          target: 'ready',
          actions: [{ type: 'logErrorEvent', params: { operation: 'fetchCartData (refetch)' } }],
        },
      },
    },
    joiningUserToMultiCart: {
      invoke: {
        src: 'joinUserToMultiCart',
        input: ({ event }) => {
          assertEvent(event, 'join multi cart')
          return {
            sharedCartId: event.sharedCartId,
          }
        },
        onDone: {
          target: 'initializing',
          actions: [
            'showJoinCartSuccessSnackbar',
            'pushToCartPage',
            { type: 'assignActiveCartId', params: ({ event }: { event: DoneActorEvent<{ newlyJoinedCartId: string }, string> }) => ({ cartId: event.output.newlyJoinedCartId }) },
          ],
        },
        onError: {
          target: 'ready',
          actions: [
            { type: 'logErrorEvent', params: { operation: 'joinUserToMultiCart' } },
            'showJoinCartErrorSnackbar',
            'pushToCartPage',
          ],
        },
      },
    },
    movingItemsToTargetCart: {
      invoke: {
        src: 'moveItemsToTargetCart',
        input: ({ event, context }) => {
          assertEvent(event, 'select and move items to target cart')

          return {
            sourceCartId: context.activeCartId!,
            targetCartId: event.targetCartId,
            itemIdsToMove: event.multiCartItemIdsToMove,
          }
        },
        onDone: {
          target: 'fetchingCartData',
          actions: [
            { type: 'assignActiveCartId', params: ({ event }: { event: DoneActorEvent<{ targetCartId: string }, string> }) => ({ cartId: event.output.targetCartId }) },
            { type: 'removeItemFromCart', params: ({ event }: { event: DoneActorEvent<{ cartId: string, itemIds: Array<string> }, string> }) => ({ cartId: event.output.cartId, itemIds: event.output.itemIds }) },
            'showMoveItemsToCartSuccessSnackbar',
            'cartUpdated',
          ],
        },
        onError: {
          target: 'ready',
          actions: [
            { type: 'logErrorEvent', params: { operation: 'moveItemsToTargetCart' } },
            'showAddItemsToCartErrorSnackbar',
          ],
        },
      },
    },
    purchasingActiveCartItems: {
      invoke: {
        src: 'purchaseMultiCartItems',
        input: ({ event, context }) => {
          assertEvent(event, 'purchase active cart items')
          return {
            multiCartId: context.activeCartId!,
            checkoutCartId: event.checkoutCartId,
            orderId: event.orderId,
          }
        },
        onDone: {
          target: 'initializing',
          actions: ['cartUpdated'],
        },
        onError: {
          target: 'ready',
          actions: [{ type: 'logErrorEvent', params: { operation: 'purchaseMultiCartItems' } }],
        },
      },
    },
    fetchingActiveComparison: {
      invoke: {
        src: 'fetchMultiCartActiveComparisons',
        onDone: {
          target: 'ready',
          actions: 'assignActiveComparison',
        },
        onError: {
          target: 'ready',
          actions: [
            { type: 'logErrorEvent', params: { operation: 'fetchMultiCartActiveComparisons' } },
            'assignActiveComparisonError',
          ],
        },
      },
    },
    creatingCartOfferComparison: {
      invoke: {
        src: 'createCartOfferComparison',
        input: ({ event, context }) => {
          assertEvent(event, 'create cart offer comparison')
          const cartId = event.cartId
          const spoofedSelections = context.cartSpoofedSelections.status === 'loaded' ? context.cartSpoofedSelections.data[cartId] || [] : []

          return {
            cartId,
            selections: spoofedSelections,
          }
        },
        onDone: {
          target: 'ready',
          actions: [
            'assignActiveComparisonFromCreation',
            'showCreateCartOfferComparisonSuccessSnackbar',
          ],
        },
        onError: {
          target: 'ready',
          actions: [
            { type: 'logErrorEvent', params: { operation: 'createCartOfferComparison' } },
            'showCreateCartOfferComparisonErrorSnackbar',
          ],
        },
      },
    },
  },
})

export default multiCartMachine
